import { formatDateTime, timeAgo } from '../../utils/format';

/** Pure helpers turning coupon-package data into what the screen renders. */

/** Level colours from the design (L1 mint, L2 forest, L3 amber, L4 purple), repeating if there are more tiers. */
export const LEVEL_STYLES = [
  { dot: 'bg-secondary', badge: 'bg-secondary', border: 'border-secondary', chip: 'bg-secondary/10 text-secondary' },
  { dot: 'bg-primary', badge: 'bg-primary', border: 'border-primary', chip: 'bg-primary/10 text-primary' },
  { dot: 'bg-amber-600', badge: 'bg-amber-600', border: 'border-amber-600', chip: 'bg-amber-600/10 text-amber-700' },
  { dot: 'bg-purple-600', badge: 'bg-purple-600', border: 'border-purple-600', chip: 'bg-purple-600/10 text-purple-700' },
];
export const levelStyle = (level) => LEVEL_STYLES[(level - 1) % LEVEL_STYLES.length] ?? LEVEL_STYLES[0];

export const STATUS_META = {
  active: { label: 'Active', tone: 'ok' },
  draft: { label: 'Draft', tone: 'neutral' },
  archived: { label: 'Archived', tone: 'archived' },
};

/** Tooltip on actions that can't apply to the active package (delete, move to draft/archived). */
export const ACTIVE_LOCK_HINT = 'Make another package active first';

export const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'draft', label: 'Draft' },
  { key: 'archived', label: 'Archived' },
];

/** Logos may be http or https (the backend accepts both); anything else is dropped. */
export function httpImageUrl(url) {
  if (typeof url !== 'string' || !url.trim()) return null;
  try {
    const parsed = new URL(url.trim());
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.href : null;
  } catch {
    return null;
  }
}

/** ISO UTC timestamp → local date-time, relative hint ("2h ago"). */
export function describeTime(iso) {
  const exact = iso ? formatDateTime(iso) : '';
  return { exact: exact || '—', relative: exact ? timeAgo(iso) : '' };
}

/** "Package #1" / "Summer 2026", or a placeholder for unnamed packages. */
export const packageTitle = (pkg) => pkg?.name?.trim() || 'Untitled package';

/** Number of reward levels: the tiers from the API, or whatever the package has. */
export function levelCount(tiers, pkg) {
  return Math.max(tiers.length, ...(pkg?.levels ?? []).map((l) => l.level), 0);
}

/** Tier for a level, falling back to "Level N" if the backend didn't send it. */
export function getTier(tiers, level) {
  return tiers.find((t) => t.level === level) ?? { level, scansRequired: null, name: `Level ${level}` };
}

/**
 * One entry per tier, in level order; levels the package doesn't have yet are marked `missing`.
 * @param {import('./couponSchema').Package} pkg
 */
export function getLevels(pkg, tiers = []) {
  return Array.from({ length: levelCount(tiers, pkg) }, (_, i) => {
    const level = i + 1;
    const found = pkg.levels.find((l) => l.level === level);
    const tier = getTier(tiers, level);
    return {
      level,
      scansRequired: tier.scansRequired ?? found?.scansRequired ?? null,
      tierName: tier.name ?? found?.tierName ?? null,
      brandName: found?.brandName?.trim() || '—',
      offerText: found?.offerText?.trim() || '—',
      offerSubtitle: found?.offerSubtitle?.trim() || null,
      couponCode: found?.couponCode?.trim() || null,
      validForDays: found?.validForDays ?? null,
      logoUrl: httpImageUrl(found?.logoUrl),
      missing: !found,
    };
  });
}

/** Today + `days` as a short local date, for the "Valid until …" preview. */
export function validUntil(days, from = new Date()) {
  if (!days) return null;
  const date = new Date(from);
  date.setDate(date.getDate() + days);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
